import { useQuery } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import type { FeatureFlagName } from "../FeatureFlagName";

export function getQueryKey(orgId?: string) {
  if (orgId !== undefined) {
    return ["featureFlagStates", orgId];
  }
  return ["featureFlagStates"];
}

/**
 * Returns the current org's feature flag states, keyed by flag name.
 *
 * Backed by a single app-level TanStack Query (60s refresh) that every
 * caller shares. The record is empty while loading or after a failure,
 * so per-flag lookups fail closed (`false`).
 */
export function useFeatureFlagStates(): Partial<
  Record<FeatureFlagName, boolean>
> {
  const { featureFlagService } = useDomainServices();
  const { currentOrganization } = useAuth();

  const orgId = currentOrganization?.org_id;

  // TODO(dave): we could cache the feature flag states in localStorage to improve the flag accuracy during initial load.
  const { data } = useQuery<Record<string, boolean>, Error>({
    queryKey: getQueryKey(orgId),
    queryFn({ signal }) {
      if (!orgId) {
        return Promise.reject(new Error("No organization selected"));
      }
      return featureFlagService.checkAllFeatureFlags(orgId, {
        abortSignal: signal,
      });
    },
    enabled: orgId !== undefined,
    staleTime: 60_000,
    refetchInterval: 60_000,
  });

  return data ?? {};
}

/**
 * Check whether a single feature flag is enabled for the current user + org.
 *
 * Backed by a shared app-level cache of all flag states — does not make a
 * per-flag network request.
 */
export function useCheckFeatureFlag(flagName: FeatureFlagName): boolean {
  return useFeatureFlagStates()[flagName] ?? false;
}
